import { useEffect, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  CampaignOccurrence,
  Campaign,
  MonthlyOfferDraft,
  MonthlyOfferItemDraft,
  MonthlyOfferOccurrenceDraft,
  PlatformKey,
  SaveMonthlyOfferResponse,
  UploadMonthlyOfferResponse,
} from '@clarity/contract';
import {
  useMonthlyOffers,
  useMonthlyReviewPublishStatus,
  usePublishMonthlyReview,
  useSaveMonthlyOffer,
  useUploadMonthlyOffer,
} from '../../queries/index.js';
import { useI18n } from '../../lib/i18n.js';
import { num, pct } from '../format.js';

/**
 * Admin-only monthly-offer review surface (clarity-campaign-matching task 13.5;
 * Req 3.3, 5.2, 10.3), built in the teal-glass world the agreed mock
 * (`brand/campaigns-mock.html`) pins — the admin-review counterpart of the
 * client-facing campaigns surface. The AM offer sheet becomes a shared,
 * effective-dated monthly catalog for a whole platform's stores.
 *
 * The flow mirrors design §8/§11:
 *  1. PICK a platform + UPLOAD an offer-sheet image. The file is read to a
 *     base64 string + its mime-type and POSTed to the key-gated OCR endpoint
 *     ({@link useUploadMonthlyOffer}); the server returns a {@link
 *     MonthlyOfferDraft}. With no OCR key the `provider` is `'noop'` and the
 *     draft is clean-EMPTY (`offers: []`) — the admin enters offers by hand; we
 *     say so plainly rather than erroring.
 *  2. REVIEW / CORRECT the draft in an editable form: per offer the name /
 *     Arabic name / audience / discount% / cap / co-fund% / min-order + the
 *     `platformDealKey` TAG (the monthly↔platform dedupe link, Req 5.2); per
 *     occurrence the start/end dates + nullable per-occurrence overrides. Offers
 *     and occurrences can be added/removed.
 *  3. SAVE → POST the corrected draft ({@link useSaveMonthlyOffer}); the server
 *     normalizes + persists it SHARED across the platform's stores,
 *     effective-dated, and returns the persisted campaigns + occurrences + any
 *     adapter warnings, which we render as the confirmation.
 *
 * This surface is admin-only: the route guard in `AppRoutes` is the real gate (a
 * client never reaches it) and the hidden "Manage" nav entry is the convenience;
 * the server independently enforces admin-only on both endpoints (Req 10.3).
 * RTL-safe (logical properties), `--font-num` tabular figures, WCAG 2.1 AA
 * (labelled inputs, accessible file picker, `role="status"`/`role="alert"` for
 * the save/empty announcements).
 */
/** The list's platform filter: all platforms, or one. */
type PlatformFilter = 'all' | PlatformKey;

export function MonthlyOfferReview() {
  const { t } = useI18n();
  // The list is the primary surface; the two actions open modals.
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [modal, setModal] = useState<'add' | 'import' | null>(null);

  return (
    <section className="offer-review" aria-label={t('offerReview.title')}>
      <div className="page-head offer-review__head">
        <div>
          <h2>{t('offerReview.title')}</h2>
          <p className="sub">{t('offerReview.sub')}</p>
        </div>
        <div className="offer-review__actions">
          <button type="button" className="ghost" onClick={() => setModal('add')}>
            {t('offerReview.action.add')}
          </button>
          <button type="button" className="primary" onClick={() => setModal('import')}>
            {t('offerReview.action.import')}
          </button>
        </div>
      </div>

      {/* Publish-to-owners (clarity-monthly-campaign-review Req 6): the explicit
          step that fans out each enabled store's WhatsApp review. */}
      <PublishToOwners
        platformKey={platformFilter === 'all' ? 'talabat' : platformFilter}
      />

      {/* The saved catalog — primary content, filterable by platform. */}
      <CurrentOffers platformFilter={platformFilter} onPlatformFilter={setPlatformFilter} />

      {modal === 'add' && <AddOfferModal onClose={() => setModal(null)} />}
      {modal === 'import' && <ImportOffersModal onClose={() => setModal(null)} />}
    </section>
  );
}

// ── Publish to owners (clarity-monthly-campaign-review Req 6) ────────────────

/** The current month as "YYYY-MM" (the default publish target). */
function currentMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * The admin "Publish to owners" surface (clarity-monthly-campaign-review Req 6),
 * built to `brand/monthly-review-publish-mock.html`. A draft/published status
 * banner, a fan-out recap (enabled stores / owners / the co-fund caveat), and a
 * confirm dialog before sending. Publishing fans out ONE WhatsApp review per
 * enabled store through the server dispatcher; re-publish is change-only. The
 * server guard is the real admin gate; this surface is reached only via the
 * admin-gated route. Bilingual + RTL (logical props, `role=status`/`alert`).
 */
function PublishToOwners({ platformKey }: { platformKey: PlatformKey }) {
  const { t } = useI18n();
  const [month, setMonth] = useState(currentMonth());
  const [confirming, setConfirming] = useState(false);

  const status = useMonthlyReviewPublishStatus(platformKey, month);
  const publish = usePublishMonthlyReview(platformKey, month);

  const body = status.data?.status === 200 ? status.data.body : null;
  const published = body?.state === 'published';
  const storeCount = body?.storeCount ?? 0;
  const result = publish.data?.status === 200 ? publish.data.body : null;

  function doPublish() {
    setConfirming(false);
    publish.mutate({ params: { platformKey, month }, body: {} });
  }

  return (
    <section className="card offer-review__publish" aria-label={t('review.publish.head')}>
      <div className="offer-review__publish-head">
        <div>
          <h3>{t('review.publish.head')}</h3>
          <p className="sub muted">{t('review.publish.sub')}</p>
        </div>
        <label className="offer-review__platform">
          {t('review.publish.month')}
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value || currentMonth())}
            aria-label={t('review.publish.month')}
          />
        </label>
      </div>

      <div
        className={`offer-review__publish-banner offer-review__publish-banner--${published ? 'published' : 'draft'}`}
        role="status"
      >
        {published
          ? t('review.publish.status.published', { when: (body?.lastPublishedAt ?? '').slice(0, 10) })
          : t('review.publish.status.draft')}
      </div>

      <p className="offer-review__publish-recap num">
        {t('review.publish.recap', { stores: num(storeCount), owners: num(storeCount) })}
      </p>
      <p className="muted offer-review__publish-optout">{t('review.publish.optOutNote')}</p>

      <div className="offer-review__editor-actions">
        <button
          type="button"
          className="primary"
          onClick={() => setConfirming(true)}
          disabled={publish.isPending || storeCount === 0}
        >
          {publish.isPending ? t('review.publish.action.pending') : t('review.publish.action')}
        </button>
      </div>

      {result && (
        <p className="offer-review__status" role="status">
          {t('review.publish.done', { dispatched: num(result.dispatched), skipped: num(result.skipped) })}
        </p>
      )}
      {publish.isError && (
        <p className="error" role="alert">
          {t('review.publish.error')}
        </p>
      )}

      {confirming && (
        <Modal
          title={t('review.publish.confirm.title')}
          sub={t('review.publish.confirm.body', { owners: num(storeCount), stores: num(storeCount) })}
          onClose={() => setConfirming(false)}
        >
          <div className="offer-review__editor-actions">
            <button type="button" className="ghost" onClick={() => setConfirming(false)}>
              {t('review.publish.confirm.cancel')}
            </button>
            <button type="button" className="primary" onClick={doPublish}>
              {t('review.publish.confirm.go')}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

// ── Import modal (upload → OCR draft → review → save) ────────────────────────

/**
 * The IMPORT flow in a modal: pick a platform + upload an offer-sheet image →
 * key-gated OCR → the editable {@link DraftEditor} → save. This is the original
 * intake/review/save flow, scoped to a dialog so the list stays the primary
 * surface. On a successful save the result + warnings render.
 */
function ImportOffersModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const upload = useUploadMonthlyOffer();
  const save = useSaveMonthlyOffer();

  const [platformKey, setPlatformKey] = useState<PlatformKey>('talabat');
  const [draft, setDraft] = useState<MonthlyOfferDraft | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const saved: SaveMonthlyOfferResponse | null =
    save.data?.status === 200 ? save.data.body : null;

  async function onFilePicked(file: File) {
    setFileName(file.name);
    const { base64, mimeType } = await readFileAsBase64(file);
    upload.mutate(
      { body: { platformKey, imageBase64: base64, mimeType } },
      {
        onSuccess: (res) => {
          if (res.status !== 200) return;
          const body = res.body as UploadMonthlyOfferResponse;
          setProvider(body.provider);
          setDraft(body.draft);
          save.reset();
        },
      },
    );
  }

  function updateDraft(next: MonthlyOfferDraft) {
    setDraft(next);
    if (save.data) save.reset();
  }

  const emptyDraft = draft !== null && draft.offers.length === 0;

  return (
    <Modal title={t('offerReview.import.title')} sub={t('offerReview.import.sub')} onClose={onClose}>
      {/* Only show the intake until a draft lands; then the modal is the review. */}
      {draft === null && (
        <div className="offer-review__import-intake">
          <label className="offer-review__platform offer-review__platform--block">
            {t('offerReview.platform')}
            <select
              value={platformKey}
              onChange={(e) => setPlatformKey(e.target.value as PlatformKey)}
            >
              <option value="talabat">Talabat</option>
              <option value="careem">Careem</option>
            </select>
          </label>

          {/* The dropzone IS the file picker — the whole area is the label. */}
          <label className="offer-review__dropzone">
            <input
              type="file"
              accept="image/*"
              className="offer-review__dropzone-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onFilePicked(file);
              }}
            />
            <span className="offer-review__dropzone-title">
              {fileName ?? t('offerReview.import.dropTitle')}
            </span>
            <span className="offer-review__dropzone-hint muted">
              {t('offerReview.import.dropHint')}
            </span>
          </label>

          {upload.isPending && (
            <p className="offer-review__status" role="status">
              {t('offerReview.ocr.pending')}
            </p>
          )}
          {upload.isError && (
            <p className="error" role="alert">
              {t('offerReview.ocr.error')}
            </p>
          )}
        </div>
      )}

      {draft !== null && provider !== null && (
        <OcrProviderBanner provider={provider} empty={emptyDraft} />
      )}

      {draft !== null && (
        <DraftEditor
          draft={draft}
          onChange={updateDraft}
          onSave={() => draft && save.mutate({ body: { platformKey, draft } })}
          saving={save.isPending}
          saveError={save.isError}
        />
      )}

      {saved && <SaveResult result={saved} platformKey={platformKey} />}
    </Modal>
  );
}

// ── Add modal (one offer, by hand) ───────────────────────────────────────────

/**
 * The ADD flow in a modal: enter ONE monthly offer by hand (its terms + the run
 * dates) and save it for the chosen platform. Reuses the same
 * {@link DraftEditor}, seeded with a single empty offer and `singleOffer` so the
 * add/remove-offer affordances are hidden — the admin fills the terms (incl. the
 * optional declared range) + at least one date, then saves.
 */
function AddOfferModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const save = useSaveMonthlyOffer();

  const [platformKey, setPlatformKey] = useState<PlatformKey>('talabat');
  const [draft, setDraft] = useState<MonthlyOfferDraft>({ offers: [emptyOffer()] });

  const saved: SaveMonthlyOfferResponse | null =
    save.data?.status === 200 ? save.data.body : null;

  function updateDraft(next: MonthlyOfferDraft) {
    setDraft(next);
    if (save.data) save.reset();
  }

  return (
    <Modal title={t('offerReview.add.title')} sub={t('offerReview.add.sub')} onClose={onClose}>
      <label className="offer-review__platform offer-review__platform--block">
        {t('offerReview.platform')}
        <select
          value={platformKey}
          onChange={(e) => setPlatformKey(e.target.value as PlatformKey)}
        >
          <option value="talabat">Talabat</option>
          <option value="careem">Careem</option>
        </select>
      </label>

      <DraftEditor
        draft={draft}
        onChange={updateDraft}
        onSave={() => save.mutate({ body: { platformKey, draft } })}
        saving={save.isPending}
        saveError={save.isError}
        singleOffer
      />

      {saved && <SaveResult result={saved} platformKey={platformKey} />}
    </Modal>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────

/**
 * A small accessible modal shell (`role="dialog"`, labelled, Escape-to-close,
 * backdrop-click-to-close). Local to this surface — the admin catalog is the
 * only consumer; extract a shared primitive later if a second surface needs it.
 */
function Modal({
  title,
  sub,
  onClose,
  children,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="offer-review__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="offer-review__modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="offer-review__modal-head">
          <div>
            <h3 id={titleId}>{title}</h3>
            {sub && <p className="sub muted">{sub}</p>}
          </div>
          <button
            type="button"
            className="offer-review__modal-close"
            onClick={onClose}
            aria-label={t('offerReview.modal.close')}
          >
            ×
          </button>
        </div>
        <div className="offer-review__modal-body">{children}</div>
      </div>
    </div>
  );
}

// ── Current offers catalog (read of what's already saved) ────────────────────

/**
 * The CURRENT offers catalog for the selected platform — a read of the saved,
 * platform-shared monthly offers (`useMonthlyOffers`), so the admin sees what's
 * live instead of an empty uploader. Each offer is an expandable row showing its
 * terms (the discount or declared range + cap), audience, and run count; expand
 * to its occurrences with any per-run overrides. Read-only (the review/save flow
 * above is how offers are created/corrected). Reacts to the platform picker.
 */
function CurrentOffers({
  platformFilter,
  onPlatformFilter,
}: {
  platformFilter: PlatformFilter;
  onPlatformFilter: (next: PlatformFilter) => void;
}) {
  const { t } = useI18n();
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});

  // The route is per-platform; "All" merges both. Both queries run always
  // (hooks are unconditional); we select which to show from the active filter.
  const talabat = useMonthlyOffers('talabat');
  const careem = useMonthlyOffers('careem');

  const sources =
    platformFilter === 'all'
      ? ([['talabat', talabat] as const, ['careem', careem] as const])
      : platformFilter === 'talabat'
        ? ([['talabat', talabat] as const])
        : ([['careem', careem] as const]);

  const isLoading = sources.some(([, q]) => q.isLoading);
  const isError = sources.some(([, q]) => q.isError);

  // Each campaign carries its platform tag so "All" can badge rows + merge.
  const campaigns: TaggedCampaign[] = useMemo(() => {
    const out: TaggedCampaign[] = [];
    for (const [key, q] of sources) {
      if (q.data?.status === 200) {
        for (const c of q.data.body.campaigns) out.push({ campaign: c, platformKey: key });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformFilter, talabat.data, careem.data]);

  const occurrences: CampaignOccurrence[] = useMemo(() => {
    const out: CampaignOccurrence[] = [];
    for (const [, q] of sources) {
      if (q.data?.status === 200) out.push(...q.data.body.occurrences);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformFilter, talabat.data, careem.data]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q === '') return campaigns;
    return campaigns.filter(
      (c) =>
        c.campaign.name.toLowerCase().includes(q) ||
        (c.campaign.arabicName ?? '').includes(filter.trim()),
    );
  }, [campaigns, filter]);

  return (
    <div className="card offer-review__current">
      <div className="offer-review__list-head">
        <div
          className="offer-review__seg"
          role="group"
          aria-label={t('offerReview.current.filterPlatform')}
        >
          {(['all', 'talabat', 'careem'] as const).map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={platformFilter === p}
              onClick={() => onPlatformFilter(p)}
            >
              {p === 'all'
                ? t('offerReview.current.allPlatforms')
                : p === 'talabat'
                  ? 'Talabat'
                  : 'Careem'}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="offer-review__filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('offerReview.current.filter')}
          aria-label={t('offerReview.current.filter')}
        />
      </div>

      {!isLoading && !isError && campaigns.length > 0 && (
        <p className="offer-review__eff">
          <span className="offer-review__eff-dot" aria-hidden="true" />
          {t('offerReview.current.effNote', {
            count:
              filtered.length === 1
                ? t('offerReview.current.one')
                : t('offerReview.current.many', { count: num(filtered.length) }),
          })}
        </p>
      )}

      {isLoading && (
        <p className="offer-review__status muted" role="status">
          {t('offerReview.current.loading')}
        </p>
      )}
      {isError && (
        <p className="error" role="alert">
          {t('offerReview.current.error')}
        </p>
      )}

      {!isLoading && !isError && campaigns.length === 0 && (
        <p className="offer-review__current-empty muted" role="status">
          {t('offerReview.current.empty')}
        </p>
      )}

      {campaigns.length > 0 &&
        (filtered.length === 0 ? (
          <p className="muted offer-review__current-empty" role="status">
            {t('offerReview.current.noFilterMatch')}
          </p>
        ) : (
          <ul className="offer-review__current-list">
            {filtered.map(({ campaign: c, platformKey }) => (
              <li key={c.id}>
                <CurrentOfferRow
                  campaign={c}
                  platformKey={platformKey}
                  showPlatform={platformFilter === 'all'}
                  occurrences={occurrences.filter((o) => o.campaignId === c.id)}
                  open={open[c.id] ?? false}
                  onToggle={() => setOpen((s) => ({ ...s, [c.id]: !s[c.id] }))}
                />
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}

/** A saved campaign tagged with the platform it belongs to (for "All" merge). */
interface TaggedCampaign {
  campaign: Campaign;
  platformKey: PlatformKey;
}

/** One expandable saved-offer row: terms + audience + run count → occurrences. */
function CurrentOfferRow({
  campaign: c,
  platformKey,
  showPlatform,
  occurrences,
  open,
  onToggle,
}: {
  campaign: Campaign;
  platformKey: PlatformKey;
  /** Show the platform badge (only under the "All platforms" filter). */
  showPlatform: boolean;
  occurrences: CampaignOccurrence[];
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useI18n();
  const ranged =
    c.discountPctMin !== null && c.discountPctMax !== null && c.discountPctMax > c.discountPctMin;
  // A stated discount reads "15–50% off"; an offer with no figure just reads
  // "no set discount" (no trailing "off").
  const hasDiscount = c.discountPct !== null || (c.discountPctMin !== null && c.discountPctMax !== null);
  const disc = currentDiscountLabel(c, t);
  const terms: string[] = [hasDiscount ? t('offerReview.current.off', { disc }) : disc];
  if (c.maxDiscountCap !== null) terms.push(t('offerReview.current.cap', { cap: num(c.maxDiscountCap) }));

  const runs = occurrences.length;
  const audBadge =
    c.audience === 'plus'
      ? t('offerReview.current.tproOnly')
      : c.audience === 'nonplus'
        ? t('offerReview.current.newOnly')
        : null;

  return (
    <div className={`offer-review__ofr${open ? ' offer-review__ofr--open' : ''}`}>
      <button
        type="button"
        className="offer-review__ofr-top"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="offer-review__ofr-name">
          {c.name}
          {c.arabicName && (
            <span className="camp-name__ar" dir="rtl">
              {c.arabicName}
            </span>
          )}
          {audBadge && <span className="offer-review__pill offer-review__pill--aud">{audBadge}</span>}
          {ranged && (
            <span className="offer-review__pill offer-review__pill--range">
              {t('offerReview.current.ranged')}
            </span>
          )}
          {c.appliesTo && c.appliesTo.length > 0 && !c.appliesTo.includes('general') && (
            <span
              className="offer-review__pill offer-review__pill--cat"
              title="Applies only to these restaurant categories"
            >
              {c.appliesTo.join(', ')}
            </span>
          )}
          {c.priority && c.priority !== 'medium' && (
            <span
              className={`offer-review__pill offer-review__pill--prio offer-review__pill--prio-${c.priority}`}
              title={
                c.priority === 'low'
                  ? 'Always-on offer — yields to a dated special on overlapping days'
                  : 'High priority — wins over other offers on overlapping days'
              }
            >
              {c.priority === 'low' ? 'always-on' : 'priority'}
            </span>
          )}
        </span>
        {showPlatform ? (
          <span className={`offer-review__plat offer-review__plat--${platformKey}`}>
            <span className="offer-review__plat-dot" aria-hidden="true" />
            {platformKey === 'talabat' ? 'Talabat' : 'Careem'}
          </span>
        ) : (
          <span />
        )}
        <span className="offer-review__ofr-terms num">{terms.join(' · ')}</span>
        <span className="offer-review__ofr-runs num">
          {runs === 1
            ? t('offerReview.current.runs.one')
            : t('offerReview.current.runs.many', { count: num(runs) })}
        </span>
        <span className="offer-review__ofr-chev" aria-hidden="true">
          ▶
        </span>
      </button>

      {open && (
        <div className="offer-review__ofr-body">
          <table className="offer-review__occ-table">
            <thead>
              <tr>
                <th>{t('offerReview.current.occCol')}</th>
                <th>{t('offerReview.current.occOverrideCol')}</th>
              </tr>
            </thead>
            <tbody>
              {occurrences.map((o) => (
                <tr key={o.id}>
                  <td className="num">{occurrenceWindowLabel(o)}</td>
                  <td>{occurrenceOverrideLabel(o, c, t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** The translate fn's shape, narrowed to what these helpers need. */
type T = (key: string, vars?: Record<string, string | number>) => string;

/** The saved offer's discount term: "15–50%" for a range, "50%" point, or none. */
function currentDiscountLabel(c: Campaign, t: T): string {
  if (c.discountPctMin !== null && c.discountPctMax !== null && c.discountPctMax > c.discountPctMin) {
    return `${Math.round(c.discountPctMin * 100)}–${pct(Math.round(c.discountPctMax * 100))}`;
  }
  if (c.discountPct !== null) return pct(Math.round(c.discountPct * 100));
  return t('offerReview.current.noDiscount');
}

/** A compact date-window label for an occurrence row (YYYY-MM-DD → readable). */
function occurrenceWindowLabel(o: CampaignOccurrence): string {
  const start = o.startDate.slice(0, 10);
  const end = o.endDate.slice(0, 10);
  return start === end ? start : `${start} → ${end}`;
}

/** Name any per-run override vs the parent offer, else "inherits offer". */
function occurrenceOverrideLabel(o: CampaignOccurrence, c: Campaign, t: T): string {
  const parts: string[] = [];
  if (o.discountPct !== null && o.discountPct !== c.discountPct) {
    parts.push(`${Math.round(o.discountPct * 100)}%`);
  }
  if (o.maxDiscountCap !== null && o.maxDiscountCap !== c.maxDiscountCap) {
    parts.push(`cap ${num(o.maxDiscountCap)} JOD`);
  }
  if (o.audience !== null && o.audience !== c.audience) parts.push(o.audience);
  if (o.minOrder !== null && o.minOrder !== c.minOrder) parts.push(`min ${num(o.minOrder)} JOD`);
  return parts.length > 0 ? parts.join(' · ') : t('offerReview.current.inherits');
}

/**
 * The provider banner. With the OCR key configured the sheet was transcribed; a
 * `'noop'` provider (no key) means a clean-empty draft the admin fills in — we
 * say so explicitly so the empty form never reads as a failure (Req 3.2).
 */
function OcrProviderBanner({ provider, empty }: { provider: string; empty: boolean }) {
  const { t } = useI18n();
  const noKey = provider === 'noop';
  return (
    <div
      className={`offer-review__banner ${noKey ? 'offer-review__banner--manual' : 'offer-review__banner--ocr'}`}
      role="status"
    >
      {noKey
        ? empty
          ? t('offerReview.banner.noKey')
          : t('offerReview.banner.noKeyNonEmpty')
        : t('offerReview.banner.ocr')}
    </div>
  );
}

// ── The editable draft form ───────────────────────────────────────────────────

/**
 * The review/correct form: one editable card per offer (its terms + the
 * `platformDealKey` tag + its occurrences), add/remove affordances for both
 * offers and occurrences, and the Save action. Edits are applied immutably back
 * through `onChange` so the parent holds the single working draft.
 */
function DraftEditor({
  draft,
  onChange,
  onSave,
  saving,
  saveError,
  singleOffer = false,
}: {
  draft: MonthlyOfferDraft;
  onChange: (next: MonthlyOfferDraft) => void;
  onSave: () => void;
  saving: boolean;
  saveError: boolean;
  /** The Add-by-hand flow: one offer, so hide the add/remove-offer affordances. */
  singleOffer?: boolean;
}) {
  const { t } = useI18n();

  function setOffer(index: number, offer: MonthlyOfferItemDraft) {
    const offers = draft.offers.map((o, i) => (i === index ? offer : o));
    onChange({ offers });
  }
  function addOffer() {
    onChange({ offers: [...draft.offers, emptyOffer()] });
  }
  function removeOffer(index: number) {
    onChange({ offers: draft.offers.filter((_, i) => i !== index) });
  }

  return (
    <div className="offer-review__editor">
      {!singleOffer && (
        <div className="offer-review__editor-head">
          <h3>{t('offerReview.editor.title')}</h3>
          <span className="muted offer-review__count">
            {t(draft.offers.length === 1 ? 'offerReview.editor.count.one' : 'offerReview.editor.count.many', {
              count: num(draft.offers.length),
            })}
          </span>
        </div>
      )}

      {draft.offers.length === 0 ? (
        <div className="card empty" role="status">
          <p className="empty__sub">{t('offerReview.editor.empty')}</p>
        </div>
      ) : (
        <ol className="offer-review__offers">
          {draft.offers.map((offer, i) => (
            <li key={i}>
              <OfferCard
                offer={offer}
                index={i}
                onChange={(o) => setOffer(i, o)}
                onRemove={() => removeOffer(i)}
                hideRemove={singleOffer}
              />
            </li>
          ))}
        </ol>
      )}

      <div className="offer-review__editor-actions">
        {!singleOffer && (
          <button type="button" className="ghost" onClick={addOffer}>
            {t('offerReview.offer.add')}
          </button>
        )}
        <button
          type="button"
          className="primary"
          onClick={onSave}
          disabled={saving || draft.offers.length === 0}
        >
          {saving ? t('offerReview.save.pending') : t('offerReview.save')}
        </button>
      </div>

      {saveError && (
        <p className="error" role="alert">
          {t('offerReview.save.error')}
        </p>
      )}
    </div>
  );
}

/**
 * One offer's editable card: name / Arabic name / audience / discount% / cap /
 * co-fund% / min-order + the `platformDealKey` tag input (Req 5.2) + the
 * occurrences editor. Every input is labelled; percentage fields are expressed as
 * whole percents in the UI (0–100) and stored as the fraction the draft/adapter
 * expect (0–1).
 */
function OfferCard({
  offer,
  index,
  onChange,
  onRemove,
  hideRemove = false,
}: {
  offer: MonthlyOfferItemDraft;
  index: number;
  onChange: (offer: MonthlyOfferItemDraft) => void;
  onRemove: () => void;
  /** Hide the remove-offer button (the single-offer Add flow). */
  hideRemove?: boolean;
}) {
  const { t } = useI18n();
  const uid = useId();
  const occurrences = offer.occurrences ?? [];

  const patch = (p: Partial<MonthlyOfferItemDraft>) => onChange({ ...offer, ...p });

  function setOccurrence(i: number, occ: MonthlyOfferOccurrenceDraft) {
    patch({ occurrences: occurrences.map((o, j) => (j === i ? occ : o)) });
  }
  function addOccurrence() {
    patch({ occurrences: [...occurrences, {}] });
  }
  function removeOccurrence(i: number) {
    patch({ occurrences: occurrences.filter((_, j) => j !== i) });
  }

  return (
    <div className="card offer-review__offer" aria-label={t('offerReview.offer.aria', { n: num(index + 1) })}>
      <div className="offer-review__offer-head">
        <h4>{t('offerReview.offer.heading', { n: num(index + 1) })}</h4>
        {!hideRemove && (
          <button
            type="button"
            className="sm offer-review__remove"
            onClick={onRemove}
            aria-label={t('offerReview.offer.remove.aria', { n: num(index + 1) })}
          >
            {t('offerReview.offer.remove')}
          </button>
        )}
      </div>

      <div className="offer-review__grid">
        <label htmlFor={`${uid}-name`}>
          {t('offerReview.field.name')}
          <input
            id={`${uid}-name`}
            type="text"
            value={offer.name ?? ''}
            onChange={(e) => patch({ name: e.target.value === '' ? null : e.target.value })}
            placeholder={t('offerReview.field.name.ph')}
          />
        </label>

        <label htmlFor={`${uid}-ar`}>
          {t('offerReview.field.arabic')}
          <input
            id={`${uid}-ar`}
            type="text"
            dir="rtl"
            value={offer.arabic_name ?? ''}
            onChange={(e) => patch({ arabic_name: e.target.value === '' ? null : e.target.value })}
            placeholder={t('offerReview.field.arabic.ph')}
          />
        </label>

        <label htmlFor={`${uid}-aud`}>
          {t('offerReview.field.audience')}
          <select
            id={`${uid}-aud`}
            value={offer.audience ?? 'all'}
            onChange={(e) => patch({ audience: e.target.value })}
          >
            <option value="all">{t('offerReview.audience.all')}</option>
            <option value="plus">{t('offerReview.audience.plus')}</option>
            <option value="nonplus">{t('offerReview.audience.nonplus')}</option>
          </select>
        </label>

        {/* Discount: a single value (min only) OR a declared range (min + max).
            The floor drives `discount_pct` (what the forward model centers on);
            a non-empty max makes it a range. */}
        <label htmlFor={`${uid}-disc-min`}>
          {t('offerReview.field.discountMin')}
          <input
            id={`${uid}-disc-min`}
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step={1}
            value={fractionToPct(offer.discount_min ?? offer.discount_pct)}
            onChange={(e) => {
              const frac = pctToFraction(e.target.value);
              // Floor drives both discount_pct and discount_min (so a single
              // value still populates discount_pct for the forward model).
              patch({ discount_pct: frac, discount_min: offer.discount_max != null ? frac : null });
            }}
          />
        </label>

        <label htmlFor={`${uid}-disc-max`}>
          {t('offerReview.field.discountMax')}
          <input
            id={`${uid}-disc-max`}
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step={1}
            value={fractionToPct(offer.discount_max)}
            onChange={(e) => {
              const frac = pctToFraction(e.target.value);
              // Setting a max makes it a range: pin discount_min to the current
              // floor; clearing it reverts to a single value (min null).
              patch({
                discount_max: frac,
                discount_min: frac != null ? (offer.discount_min ?? offer.discount_pct) : null,
              });
            }}
          />
        </label>

        <label htmlFor={`${uid}-cap`}>
          {t('offerReview.field.cap')}
          <input
            id={`${uid}-cap`}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={numToStr(offer.max_discount_cap)}
            onChange={(e) => patch({ max_discount_cap: strToNum(e.target.value) })}
          />
        </label>

        <label htmlFor={`${uid}-cofund`}>
          {t('offerReview.field.cofund')}
          <input
            id={`${uid}-cofund`}
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step={1}
            value={fractionToPct(offer.co_fund_pct)}
            onChange={(e) => patch({ co_fund_pct: pctToFraction(e.target.value) })}
          />
        </label>

        <label htmlFor={`${uid}-min`}>
          {t('offerReview.field.minOrder')}
          <input
            id={`${uid}-min`}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={numToStr(offer.min_order)}
            onChange={(e) => patch({ min_order: strToNum(e.target.value) })}
          />
        </label>

        {/* The dedupe link key — tagging a monthly offer to its platform deal (Req 5.2). */}
        <div className="offer-review__deal">
          <label htmlFor={`${uid}-deal`}>
            {t('offerReview.field.dealKey')}
            <input
              id={`${uid}-deal`}
              type="text"
              value={offer.platform_deal_key ?? ''}
              aria-describedby={`${uid}-deal-hint`}
              onChange={(e) =>
                patch({ platform_deal_key: e.target.value === '' ? null : e.target.value })
              }
              placeholder={t('offerReview.field.dealKey.ph')}
            />
          </label>
          <span id={`${uid}-deal-hint`} className="offer-review__field-hint muted">
            {t('offerReview.field.dealKey.hint')}
          </span>
        </div>
      </div>

      {/* ── Occurrences (per-date runs + overrides) ── */}
      <div className="offer-review__occ">
        <div className="offer-review__occ-head">
          <h5>{t('offerReview.occ.title')}</h5>
          <span className="muted">
            {t(occurrences.length === 1 ? 'offerReview.occ.count.one' : 'offerReview.occ.count.many', {
              count: num(occurrences.length),
            })}
          </span>
        </div>

        {occurrences.length === 0 ? (
          <p className="muted offer-review__occ-empty">{t('offerReview.occ.empty')}</p>
        ) : (
          <ol className="offer-review__occ-list">
            {occurrences.map((occ, i) => (
              <li key={i}>
                <OccurrenceRow
                  occ={occ}
                  index={i}
                  onChange={(o) => setOccurrence(i, o)}
                  onRemove={() => removeOccurrence(i)}
                />
              </li>
            ))}
          </ol>
        )}

        <button type="button" className="sm" onClick={addOccurrence}>
          {t('offerReview.occ.add')}
        </button>
      </div>
    </div>
  );
}

/**
 * One occurrence row: start/end dates (required to be a real run) + the nullable
 * per-occurrence overrides (discount% / cap / co-fund% / min-order / audience).
 * A blank override inherits the parent offer — the inputs leave the value `null`
 * when empty, exactly what the draft/adapter treat as inherit.
 */
function OccurrenceRow({
  occ,
  index,
  onChange,
  onRemove,
}: {
  occ: MonthlyOfferOccurrenceDraft;
  index: number;
  onChange: (occ: MonthlyOfferOccurrenceDraft) => void;
  onRemove: () => void;
}) {
  const { t } = useI18n();
  const uid = useId();
  const patch = (p: Partial<MonthlyOfferOccurrenceDraft>) => onChange({ ...occ, ...p });

  return (
    <div className="offer-review__occ-row" aria-label={t('offerReview.occ.aria', { n: num(index + 1) })}>
      <label htmlFor={`${uid}-start`}>
        {t('offerReview.occ.start')}
        <input
          id={`${uid}-start`}
          type="date"
          value={occ.start_date ?? ''}
          onChange={(e) => patch({ start_date: e.target.value === '' ? null : e.target.value })}
        />
      </label>
      <label htmlFor={`${uid}-end`}>
        {t('offerReview.occ.end')}
        <input
          id={`${uid}-end`}
          type="date"
          value={occ.end_date ?? ''}
          onChange={(e) => patch({ end_date: e.target.value === '' ? null : e.target.value })}
        />
      </label>
      <label htmlFor={`${uid}-odisc`}>
        {t('offerReview.occ.discount')}
        <input
          id={`${uid}-odisc`}
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={1}
          value={fractionToPct(occ.discount_pct)}
          onChange={(e) => patch({ discount_pct: pctToFraction(e.target.value) })}
          placeholder={t('offerReview.occ.inherit')}
        />
      </label>
      <label htmlFor={`${uid}-ocap`}>
        {t('offerReview.occ.cap')}
        <input
          id={`${uid}-ocap`}
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={numToStr(occ.max_discount_cap)}
          onChange={(e) => patch({ max_discount_cap: strToNum(e.target.value) })}
          placeholder={t('offerReview.occ.inherit')}
        />
      </label>
      <button
        type="button"
        className="sm offer-review__remove"
        onClick={onRemove}
        aria-label={t('offerReview.occ.remove.aria', { n: num(index + 1) })}
      >
        {t('offerReview.occ.remove')}
      </button>
    </div>
  );
}

// ── The persisted result (confirmation) ───────────────────────────────────────

/**
 * The save confirmation: the persisted campaigns + occurrences (shared across
 * the platform's stores) and any adapter warnings. Announced via `role="status"`
 * so a screen reader hears the save landed.
 */
function SaveResult({
  result,
  platformKey,
}: {
  result: SaveMonthlyOfferResponse;
  platformKey: PlatformKey;
}) {
  const { t } = useI18n();
  return (
    <div className="card offer-review__result" role="status" aria-live="polite">
      <h3>{t('offerReview.result.title')}</h3>
      <p className="offer-review__result-lede">
        {t('offerReview.result.lede', {
          campaigns: num(result.campaigns.length),
          occurrences: num(result.occurrences.length),
          platform: platformKey === 'talabat' ? 'Talabat' : 'Careem',
        })}
      </p>

      {result.warnings.length > 0 && (
        <ul className="offer-review__warnings" aria-label={t('offerReview.result.warnings')}>
          {result.warnings.map((w, i) => (
            <li key={i} className="offer-review__warning">
              {w}
            </li>
          ))}
        </ul>
      )}

      {result.campaigns.length > 0 && (
        <ul className="offer-review__saved-list">
          {result.campaigns.map((c: Campaign) => (
            <li key={c.id} className="offer-review__saved">
              <span className="offer-review__saved-name">
                {c.name}
                {c.arabicName && (
                  <span className="camp-name__ar" dir="rtl">
                    {c.arabicName}
                  </span>
                )}
              </span>
              <span className="offer-review__saved-meta num">
                {occurrenceCount(c.id, result.occurrences)} ·{' '}
                {c.platformDealKey
                  ? t('offerReview.saved.tagged', { key: c.platformDealKey })
                  : t('offerReview.saved.untagged')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function occurrenceCount(campaignId: string, occurrences: CampaignOccurrence[]): string {
  const n = occurrences.filter((o) => o.campaignId === campaignId).length;
  return `${num(n)} ${n === 1 ? 'date' : 'dates'}`;
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** A fresh, empty offer with one blank occurrence to start from. */
function emptyOffer(): MonthlyOfferItemDraft {
  return {
    name: null,
    arabic_name: null,
    audience: 'all',
    discount_pct: null,
    max_discount_cap: null,
    co_fund_pct: null,
    min_order: null,
    platform_deal_key: null,
    occurrences: [{}],
  };
}

/** Read a File into `{ base64, mimeType }` — the strippable data-URL payload. */
function readFileAsBase64(file: File): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.onload = () => {
      const result = String(reader.result);
      // `data:<mime>;base64,<payload>` → strip the prefix, keep the payload.
      const comma = result.indexOf(',');
      const base64 = comma >= 0 ? result.slice(comma + 1) : result;
      resolve({ base64, mimeType: file.type || 'application/octet-stream' });
    };
    reader.readAsDataURL(file);
  });
}

/** A nullable fraction (0–1) → the whole-percent string the input shows (or ''). */
function fractionToPct(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined) return '';
  return String(Math.round(fraction * 1000) / 10);
}

/** The input's whole-percent string → a nullable fraction (0–1); '' ⇒ null. */
function pctToFraction(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return Math.round((n / 100) * 1000) / 1000;
}

/** A nullable number → its input string (or ''). */
function numToStr(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/** An input string → a nullable number; '' or NaN ⇒ null. */
function strToNum(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}
